import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mail, Send, RefreshCw, CheckCircle, AlertCircle, Code } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

/**
 * Gmail Action Node Configuration Component
 * Allows users to configure a "Send Email with Gmail" action
 */
const GmailActionConfig = ({ nodeData, onUpdate, workflowData }) => {
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(nodeData?.config?.connection_id || '');
  const [emailData, setEmailData] = useState({
    to: nodeData?.config?.to || '',
    subject: nodeData?.config?.subject || '',
    body: nodeData?.config?.body || '',
    cc: nodeData?.config?.cc || '',
    bcc: nodeData?.config?.bcc || '',
    html: nodeData?.config?.html || false
  });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showDataMapping, setShowDataMapping] = useState(false);

  // Available data fields from previous nodes
  const [availableFields, setAvailableFields] = useState([]);

  // Load Gmail connections and available data fields on mount
  useEffect(() => {
    loadConnections();
    loadAvailableFields();
  }, []);

  const loadConnections = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/workflows/nodes/gmail/connections`, {
        withCredentials: true
      });
      
      if (response.data.success) {
        setConnections(response.data.connections);
        
        // Auto-select first connection if none selected
        if (!selectedConnection && response.data.connections.length > 0) {
          setSelectedConnection(response.data.connections[0].connection_id);
        }
      }
    } catch (error) {
      console.error('Error loading Gmail connections:', error);
      toast.error('Failed to load Gmail connections');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableFields = () => {
    // Extract available fields from previous nodes in workflow
    // This would come from the workflow builder context
    const fields = [
      { name: 'email.from', description: 'Sender email address' },
      { name: 'email.to', description: 'Recipient email address' },
      { name: 'email.subject', description: 'Email subject' },
      { name: 'email.body', description: 'Email body content' },
      { name: 'email.date', description: 'Email date' },
      { name: 'email.id', description: 'Email ID' }
    ];
    setAvailableFields(fields);
  };

  const handleConnectGmail = async () => {
    try {
      // Redirect to Gmail OAuth flow
      const response = await axios.get(`${API}/oauth/google/authorize?service=gmail`, {
        withCredentials: true
      });
      
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      console.error('Error connecting Gmail:', error);
      toast.error('Failed to connect Gmail');
    }
  };

  const handleInsertField = (fieldName, targetField) => {
    // Insert field reference into the target field
    const fieldRef = `{{${fieldName}}}`;
    setEmailData(prev => ({
      ...prev,
      [targetField]: prev[targetField] + fieldRef
    }));
    toast.success(`Inserted ${fieldName}`);
  };

  const handleTestAction = async () => {
    if (!selectedConnection) {
      toast.error('Please select a Gmail connection');
      return;
    }

    if (!emailData.to || !emailData.subject || !emailData.body) {
      toast.error('Please fill in To, Subject, and Body fields');
      return;
    }

    try {
      setTesting(true);
      const response = await axios.post(
        `${API}/workflows/nodes/gmail/send-email`,
        {
          connection_id: selectedConnection,
          to: emailData.to,
          subject: emailData.subject,
          body: emailData.body,
          cc: emailData.cc || undefined,
          bcc: emailData.bcc || undefined,
          html: emailData.html
        },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setTestResult({
          success: true,
          message: 'Email sent successfully!',
          details: response.data.message
        });
        toast.success('Test email sent successfully');
      }
    } catch (error) {
      console.error('Error testing action:', error);
      setTestResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to send email'
      });
      toast.error('Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = () => {
    if (!selectedConnection) {
      toast.error('Please select a Gmail connection');
      return;
    }

    if (!emailData.to || !emailData.subject || !emailData.body) {
      toast.error('Please fill in To, Subject, and Body fields');
      return;
    }

    const config = {
      connection_id: selectedConnection,
      to: emailData.to,
      subject: emailData.subject,
      body: emailData.body,
      cc: emailData.cc || undefined,
      bcc: emailData.bcc || undefined,
      html: emailData.html
    };

    onUpdate({
      ...nodeData,
      config,
      label: `Gmail: Send Email to ${emailData.to}`
    });

    toast.success('Gmail action configured');
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-2 mb-4">
        <Send className="w-5 h-5 text-emerald-500" />
        <h3 className="text-lg font-semibold">Gmail Action: Send Email</h3>
      </div>

      {/* Gmail Connection */}
      <div className="space-y-2">
        <Label>Gmail Account</Label>
        {connections.length === 0 ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">No Gmail account connected</p>
            <Button onClick={handleConnectGmail} className="w-full">
              <Mail className="w-4 h-4 mr-2" />
              Connect Gmail
            </Button>
          </div>
        ) : (
          <Select value={selectedConnection} onValueChange={setSelectedConnection}>
            <SelectTrigger>
              <SelectValue placeholder="Select Gmail account" />
            </SelectTrigger>
            <SelectContent>
              {connections.map((conn) => (
                <SelectItem key={conn.connection_id} value={conn.connection_id}>
                  {conn.email || conn.connection_id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Email Configuration Tabs */}
      <Tabs defaultValue="compose" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="compose">Compose</TabsTrigger>
          <TabsTrigger value="mapping">Data Mapping</TabsTrigger>
        </TabsList>

        <TabsContent value="compose" className="space-y-3">
          {/* To Field */}
          <div className="space-y-2">
            <Label htmlFor="to">To <span className="text-red-500">*</span></Label>
            <Input
              id="to"
              placeholder="recipient@example.com or {{email.from}}"
              value={emailData.to}
              onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
            />
<p className="text-xs text-muted-foreground">
              Use double curly braces around field_name to insert data from previous nodes
            </p>
          </div>

          {/* CC Field */}
          <div className="space-y-2">
            <Label htmlFor="cc">CC (Optional)</Label>
            <Input
              id="cc"
              placeholder="cc@example.com"
              value={emailData.cc}
              onChange={(e) => setEmailData({ ...emailData, cc: e.target.value })}
            />
          </div>

          {/* BCC Field */}
          <div className="space-y-2">
            <Label htmlFor="bcc">BCC (Optional)</Label>
            <Input
              id="bcc"
              placeholder="bcc@example.com"
              value={emailData.bcc}
              onChange={(e) => setEmailData({ ...emailData, bcc: e.target.value })}
            />
          </div>

          {/* Subject Field */}
          <div className="space-y-2">
            <Label htmlFor="subject">Subject <span className="text-red-500">*</span></Label>
            <Input
              id="subject"
              placeholder="Email subject or {{email.subject}}"
              value={emailData.subject}
              onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
            />
          </div>

          {/* Body Field */}
          <div className="space-y-2">
            <Label htmlFor="body">Body <span className="text-red-500">*</span></Label>
            <Textarea
              id="body"
              placeholder="Email body content..."
              value={emailData.body}
              onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
              rows={8}
            />
          </div>

          {/* HTML Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="html">HTML Email</Label>
              <p className="text-xs text-muted-foreground">Send as HTML instead of plain text</p>
            </div>
            <Switch
              id="html"
              checked={emailData.html}
              onCheckedChange={(checked) => setEmailData({ ...emailData, html: checked })}
            />
          </div>
        </TabsContent>

        <TabsContent value="mapping" className="space-y-3">
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Code className="w-4 h-4" />
              Available Data Fields
            </Label>
            <p className="text-sm text-muted-foreground">
              Click a field to see how to use it in your email
            </p>
          </div>

          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {availableFields.map((field) => (
              <div
                key={field.name}
                className="p-3 border rounded-lg hover:bg-muted cursor-pointer transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <Badge variant="secondary" className="mb-1">
                      {field.name}
                    </Badge>
                    <p className="text-xs text-muted-foreground">{field.description}</p>
                  </div>
                </div>
                <div className="mt-2 space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleInsertField(field.name, 'to')}
                  >
                    Insert in To
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleInsertField(field.name, 'subject')}
                  >
                    Insert in Subject
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleInsertField(field.name, 'body')}
                  >
                    Insert in Body
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-muted p-3 rounded-lg">
            <p className="text-sm font-medium mb-1">Usage Example:</p>
<code className="text-xs">
              Subject: Re: your_email_subject_field<br />
              Body: Hi your_email_from_field, thank you for your email...
            </code>
          </div>
        </TabsContent>
      </Tabs>

      {/* Test Result */}
      {testResult && (
        <div className={`p-3 rounded-lg border ${
          testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-start gap-2">
            {testResult.success ? (
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            )}
            <div className="flex-1">
              <p className={`font-medium ${testResult.success ? 'text-green-900' : 'text-red-900'}`}>
                {testResult.message}
              </p>
              {testResult.details && (
                <div className="mt-2 text-xs bg-white p-2 rounded border">
                  <p>Message ID: {testResult.details.id}</p>
                  <p>Sent to: {testResult.details.to}</p>
                  <p>At: {new Date(testResult.details.sent_at).toLocaleString()}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 pt-4 border-t">
        <Button
          variant="outline"
          onClick={handleTestAction}
          disabled={testing || !selectedConnection}
          className="flex-1"
        >
          {testing ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Test Send
            </>
          )}
        </Button>
        <Button onClick={handleSave} disabled={!selectedConnection} className="flex-1">
          Save Configuration
        </Button>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
        <p className="text-sm text-blue-900">
          <strong>Tip:</strong> Use the Data Mapping tab to insert fields from previous nodes.
          The email will be sent when this node is executed in the workflow.
        </p>
      </div>
    </div>
  );
};

export default GmailActionConfig;
